<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { MAP_STYLES, ICON_ACTIVE } from '@/configs/map';
import MapPointCard from '@/components/cards/MapPointCard.vue';
import { useGlobalStore } from '@/stores/global';
const globalStore = useGlobalStore();
const googleMapsApiKey = computed(() => globalStore.googleMapsApiKey);

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  isCarPage: {
    type: Boolean,
    default: false,
  },
});

const centers = computed(() => props.data.items || []);
const mapEl = ref(null);
let map = null;
let markers = [];

let gmapsPromise = null;
function loadGoogleMaps(apiKey) {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.google?.maps) return Promise.resolve();
  if (gmapsPromise) return gmapsPromise;

  gmapsPromise = new Promise((resolve, reject) => {
    const id = 'gmaps-script';
    if (document.getElementById(id)) {
      const wait = () =>
        window.google?.maps ? resolve() : setTimeout(wait, 50);
      return wait();
    }
    const s = document.createElement('script');
    s.id = id;
    s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
  return gmapsPromise;
}

function initMap() {
  if (!mapEl.value || !window.google?.maps) return;

  const first = centers.value?.[0];
  const firstLat = Number(first?.marker?.lat);
  const firstLng = Number(first?.marker?.lng);
  const initialCenter =
    !Number.isNaN(firstLat) && !Number.isNaN(firstLng)
      ? { lat: firstLat, lng: firstLng }
      : { lat: 50.4501, lng: 30.5234 };
  map = new window.google.maps.Map(mapEl.value, {
    center: initialCenter,
    zoom: 11,
    styles: MAP_STYLES,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: true,
  });
}

function clearMarkers() {
  markers.forEach((m) => m.setMap(null));
  markers = [];
}

function renderMarkers() {
  if (!map) return;
  clearMarkers();

  const bounds = new window.google.maps.LatLngBounds();

  centers.value.forEach((item) => {
    const lat = Number(item?.marker?.lat);
    const lng = Number(item?.marker?.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    const marker = new window.google.maps.Marker({
      position: { lat, lng },
      map,
      icon: ICON_ACTIVE,
      title: item.name || '',
      clickable: false,
    });

    markers.push(marker);
    bounds.extend(marker.getPosition());
  });

  if (!bounds.isEmpty()) {
    map.fitBounds(bounds, 48);
    if (centers.value.length === 1) {
      const listener = window.google.maps.event.addListener(map, 'idle', () => {
        if (map.getZoom() > 15) map.setZoom(15);
        window.google.maps.event.removeListener(listener);
      });
    }
  }
}

async function tryInitMap(apiKey) {
  try {
    await loadGoogleMaps(apiKey);
    initMap();
    renderMarkers();
  } catch (error) {
    console.error('Failed to load Google Maps with dynamic API key:', error);
  }
}

// watch(...,{immediate:true}) fires synchronously during setup(), before this
// component has had its first mount — mapEl isn't bound yet at that point, and
// nextTick()/rAF don't reliably fix that since they aren't tied to this
// component's own first paint. onMounted() is the point Vue guarantees refs
// are bound, so it handles the initial load; the watcher only reacts to the
// key changing afterwards (e.g. arriving after an async settings fetch).
onMounted(() => {
  if (googleMapsApiKey.value) tryInitMap(googleMapsApiKey.value);
});

watch(
  () => googleMapsApiKey.value,
  (newKey) => {
    if (newKey) tryInitMap(newKey);
  },
);
onBeforeUnmount(() => {
  clearMarkers();
  map = null;
});
</script>

<template>
  <section
    :id="data.id"
    class="section"
    :class="[
      isCarPage
        ? 'bg-gray'
        : data?.background_color
          ? `bg-${data.background_color}`
          : '',
      isCarPage
        ? 'section--top-zero'
        : data?.spacing?.top
          ? `section--top-${data.spacing.top}`
          : '',
      isCarPage
        ? 'section--bottom-zero'
        : data?.spacing?.bottom
          ? `section--bottom-${data.spacing.bottom}`
          : '',
    ]"
  >
    <div class="container">
      <div
        :class="[
          'network',
          isCarPage
            ? 'network--gray'
            : data?.background_color
              ? `network--${data.background_color}`
              : '',
        ]"
      >
        <h2
          v-if="data.title"
          class="mb-0 network__title h2"
          data-aos="fade-up"
          data-aos-easing="ease-out-cubic"
          v-html="data.title"
        ></h2>
        <div class="network__wrap">
          <MapPointCard
            :type="'one_point'"
            :name="data.items[0]?.name"
            :marker="data.items[0]?.marker"
            :address="data.items[0]?.address"
            :phone="data.items[0]?.phone"
            :time="data.items[0]?.time"
            :note="data.items[0]?.note"
            class="network__card"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          />
          <div
            ref="mapEl"
            :class="['network__map', `network__map--${data.template}`]"
            :aria-label="data.title"
            data-aos="fade-up"
            data-aos-easing="ease-out-cubic"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.network {
  $parent: &;
  display: flex;
  flex-direction: column;
  gap: 40px;
  @include media-breakpoint-up(sm) {
    gap: 64px;
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 64);
  }
  &--white {
    #{$parent} {
    }
  }
  &--black {
    #{$parent} {
      &__map {
        background: $lime-green;
      }
    }
  }
  &--gray {
    #{$parent} {
      &__map {
        background: $lime-green;
      }
    }
  }
  &__title {
    margin: 0;
  }
  &__title {
    margin: 0;
  }
  &__wrap {
    position: relative;
  }
  &__card {
    bottom: 16px;
    height: auto;
    left: 0px;
    position: absolute;
    width: 100%;
    z-index: 5;
    @include media-breakpoint-up(sm) {
      bottom: 20px;
    }
    @include media-breakpoint-up(md) {
      bottom: unset;
      height: calc(100% - 96px);
      max-width: 384px;
      top: 48px;
    }
    @include media-breakpoint-up(xl) {
      height: calc(100% - px-to-vw(96));
      @include responsive-spacing(max-width, 384);
      @include responsive-spacing(top, 48);
    }
  }
  &__map {
    border-radius: 8px;
    height: 620px;
    margin: 0 -16px;
    overflow: hidden;
    width: calc(100% + 32px);
    &--autocenter {
      height: 864px;
    }
    @include media-breakpoint-up(sm) {
      height: 840px;
      margin: 0 -20px;
      width: calc(100% + 40px);
    }
    @include media-breakpoint-up(md) {
      height: 600px;
      margin: 0 -48px;
      width: calc(100% + 96px);
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(margin-left, -48);
      @include responsive-spacing(margin-right, -48);
      @include responsive-spacing(height, 600);
      width: calc(100% + px-to-vw(96));
    }
  }
}
</style>
